// Phase 1265: Seasonal urgency banner — auto-swap CTAs based on storm calendar
import { useMemo, useState } from "react";
import { X, AlertTriangle, Shield, Sun, CloudRain, Wind } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "obc_seasonal_banner_v2_dismissed";

interface SeasonalUrgencyBannerProps {
  state?: "FL" | "GA";
  className?: string;
}

interface SeasonalConfig {
  icon: React.ReactNode;
  headline: string;
  subtext: string;
  ctaText: string;
  ctaUrl: string;
  bgClass: string;
}

const getSeasonalConfig = (month: number, state: string): SeasonalConfig => {
  if (month >= 8 && month <= 10) {
    return {
      icon: <Wind className="h-6 w-6" />,
      headline: "Peak Hurricane Season Is Here",
      subtext: `Protect your ${state === "FL" ? "Florida" : "Georgia"} home before the next storm.`,
      ctaText: "Get Emergency Inspection",
      ctaUrl: "/emergency-roofing",
      bgClass: "bg-destructive/10 border-destructive/30",
    };
  }
  if (month >= 6 && month <= 11) {
    return {
      icon: <CloudRain className="h-6 w-6 text-primary" />,
      headline: "Storm Season Roof Check",
      subtext: "Is your roof ready? Schedule a free pre-storm inspection today.",
      ctaText: "Schedule Free Inspection",
      ctaUrl: "/services/roof-inspection",
      bgClass: "bg-primary/10 border-primary/30",
    };
  }
  if (month >= 12 || month <= 2) {
    return {
      icon: <Sun className="h-6 w-6 text-accent" />,
      headline: "Best Time for Roof Replacement",
      subtext: "Dry season = perfect installation weather. Lock in pricing before spring.",
      ctaText: "Get Free Estimate",
      ctaUrl: "/quick-estimate",
      bgClass: "bg-accent/10 border-accent/30",
    };
  }
  return {
    icon: <Shield className="h-6 w-6 text-primary" />,
    headline: "Prepare Before Storm Season",
    subtext: "Spring maintenance prevents costly hurricane damage.",
    ctaText: "Book Maintenance Check",
    ctaUrl: "/services/roof-inspection",
    bgClass: "bg-primary/10 border-primary/30",
  };
};

export const SeasonalUrgencyBanner = ({ state = "FL", className }: SeasonalUrgencyBannerProps) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "true"; } catch { return false; }
  });

  const config = useMemo(() => getSeasonalConfig(new Date().getMonth() + 1, state), [state]);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "true"); } catch {}
  };

  return (
    <div className={`border rounded-lg p-4 md:p-6 relative ${config.bgClass} ${className || ""}`}>
      <button onClick={handleDismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" aria-label="Dismiss">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start md:items-center gap-4 flex-col md:flex-row">
        <div className="flex-shrink-0">{config.icon}</div>
        <div className="flex-1">
          <h3 className="font-bold text-foreground text-lg">{config.headline}</h3>
          <p className="text-sm text-muted-foreground">{config.subtext}</p>
        </div>
        <Button asChild className="flex-shrink-0">
          <a href={config.ctaUrl}>{config.ctaText}</a>
        </Button>
      </div>
    </div>
  );
};

export default SeasonalUrgencyBanner;
